import { useContext, useEffect, useMemo } from 'react';
import { Context } from '@components/Context';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import useNavigate from '@utils/useNavigate';
import { getParams } from './history';

/**
 * A hook that redirects the user to a specified URL if they are already logged in.
 * It is typically used in login or landing pages to redirect authenticated users
 * to their dashboard or other internal pages to prevent them from seeing the login screen.
 *
 * The hook determines the logged-in status based on the state from the Context,
 * and also checks additional conditions such as terms acceptance and email verification
 * if required by the logic of the application.
 *
 * @param {boolean} emailVerification - A flag to indicate if email verification check is needed.
 * @param {string} redirectUrl - The URL to redirect to if the user is logged in. Defaults to the dashboard.
 *
 * @param ignoreRedirect
 * @returns {boolean} - A flag indicating whether the redirect condition is met (true if redirect should occur).
 *
 * Usage:
 * const redirectUrl = '/user/profile';
 * const emailVerificationNeeded = true;
 * const redirectFlag = useLoggedInRedirect(emailVerificationNeeded, redirectUrl);
 *
 * In component:
 * if (redirectFlag) {
 *   // Return null or a loading indicator as the navigation will be handled by the hook
 *   return null;
 * }
 * // Return the login or landing component if not redirected
 */
export default function useLoggedInRedirect(emailVerification: boolean = true, redirectUrl: string = '', ignoreRedirect = false): boolean {
  const { state } = useContext(Context);
  const { navigateWrapper } = useNavigate();
  const { loggedIn, firebaseLoaded, firebaseAuth } = state;
  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const params = getParams();
  const consultationParam = params.consultation === 'true';

  const redirectFlag = useMemo(() => {
    if (ignoreRedirect) {
      return false;
    }

    const emailVerificationCondition = emailVerification && !firebaseAuth?.currentUser?.emailVerified;
    return !!loggedIn && !!userInfo.termsAcceptedDate && !emailVerificationCondition;
  }, [loggedIn, userInfo.termsAcceptedDate, emailVerification, firebaseAuth?.currentUser?.emailVerified, ignoreRedirect]);

  useEffect(() => {
    if (firebaseLoaded && redirectFlag) {
      handleRedirect();
    }
  }, [firebaseLoaded, redirectFlag]);

  function handleRedirect() {
    const finalRedirectUrl = consultationParam ? '/dashboard/ppc-intro/' : redirectUrl || '/dashboard/';
    navigateWrapper(finalRedirectUrl, { replace: true });
  }

  return redirectFlag;
}
